/**
 * Helpers for Nova live updates arriving over Pusher.
 *
 * Index pages that used to poll now patch fields in place and, for main
 * points, re-rank the current page so opened rows stay on top and watching
 * rows climb as live price gets closer to entry.
 */

export function concernsResource(data, resourceName) {
  const list = data?.resources || (data?.resource ? [data.resource] : [])

  return list.includes(resourceName)
}

export function fieldByAttribute(resource, attribute) {
  if (!resource) return null

  const fields = resource.fields || []
  const hit = fields.find(field => field.attribute === attribute)
  if (hit) return hit

  for (const panel of resource.panels || []) {
    const nested = (panel.fields || []).find(field => field.attribute === attribute)
    if (nested) return nested
  }

  return null
}

export function setFieldValue(resource, attribute, value, displayedAs = undefined) {
  const field = fieldByAttribute(resource, attribute)
  if (!field) return false
  if (field.value === value && (displayedAs === undefined || field.displayedAs === displayedAs)) {
    return false
  }

  field.value = value
  if (displayedAs !== undefined) {
    field.displayedAs = displayedAs
  } else {
    field.displayedAs = value
  }

  return true
}

/**
 * Patch a main-point row from a Pusher model-updated payload (status/MTF/entry).
 *
 * @return {boolean}
 */
export function applyMainPointUpdate(resource, payload) {
  if (!payload || !resource) return false

  let changed = false

  if (payload.status != null) {
    changed = setFieldValue(resource, 'status', payload.status) || changed
  }

  if (payload.price != null) {
    changed = setFieldValue(resource, 'price', payload.price) || changed
  }

  if (payload.mtf_label != null) {
    changed = setFieldValue(
      resource,
      'mtf_label',
      payload.mtf_label,
      payload.mtf_label_display ?? payload.mtf_label,
    ) || changed
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'mtf_score')) {
    changed = setFieldValue(resource, 'mtf_score', payload.mtf_score) || changed
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'mtf_timeframes')) {
    changed = setFieldValue(resource, 'mtf_timeframes', payload.mtf_timeframes) || changed
  }

  return changed
}

export function tickFor(resource, resourceName, prices) {
  if (!prices || !resource) return null

  if (resourceName === 'coins') {
    return prices[String(resource.id?.value)]
  }

  // BelongsTo serializes the display label in `value` and the FK in `belongsToId`.
  const coinField = fieldByAttribute(resource, 'coin')
  // The index hides the BelongsTo column; the distance meter carries the coin id instead.
  const coinId =
    coinField?.belongsToId ?? coinField?.value ?? fieldByAttribute(resource, 'distance_to_entry')?.meter?.coinId

  return coinId != null ? prices[String(coinId)] : null
}

export function formatDistance(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—'

  const sign = pct > 0 ? '+' : ''

  return `${sign}${pct.toFixed(4)}%`
}

export function parseDistance(value) {
  if (value === null || value === undefined || value === '—') return null

  const parsed = parseFloat(String(value).replace(/[+,%]/g, ''))

  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Apply a batched price tick onto one serialized Nova row.
 *
 * @return {boolean} Whether any field actually changed.
 */
export function applyPrices(resource, resourceName, prices) {
  const tick = tickFor(resource, resourceName, prices)
  if (!tick) return false

  const live = Number(tick.last_price)
  if (!(live > 0)) return false

  let changed = false

  if (resourceName === 'coins') {
    changed = setFieldValue(resource, 'last_price', live) || changed
  }

  if (resourceName === 'main-points' || resourceName === 'active-main-points') {
    // live_price is a Text field (formatted string from the API).
    const label = String(live)
    changed = setFieldValue(resource, 'live_price', label, label) || changed

    const meterField = fieldByAttribute(resource, 'distance_to_entry')
    const entry = Number(fieldByAttribute(resource, 'price')?.value ?? meterField?.meter?.entry)
    if (entry > 0) {
      const pct = ((live - entry) / entry) * 100
      changed = setFieldValue(resource, 'distance_to_entry', formatDistance(pct)) || changed
    }

    // The distance meter draws from the raw tick, not the formatted label.
    if (meterField?.meter && meterField.livePrice !== live) {
      meterField.livePrice = live
      meterField.livePriceAt = tick.last_price_at || new Date().toISOString()
      changed = true
    }
  }

  return changed
}

/**
 * Keep client-side live ticks when a silent poll returns empty placeholders
 * (stale DB read or a still-hydrating price stream).
 */
export function carryLiveFields(from, to) {
  if (!from || !to) return

  for (const attribute of ['live_price', 'last_price', 'distance_to_entry']) {
    const previous = fieldByAttribute(from, attribute)
    const next = fieldByAttribute(to, attribute)
    if (!previous || !next) continue

    if (!isEmptyLiveValue(next) || isEmptyLiveValue(previous)) continue

    next.value = previous.value
    next.displayedAs = previous.displayedAs
  }

  // A silent poll reads the DB price, which can lag the stream: keep the newer client tick.
  const prevMeter = fieldByAttribute(from, 'distance_to_entry')
  const nextMeter = fieldByAttribute(to, 'distance_to_entry')
  if (prevMeter?.livePrice != null && nextMeter?.meter) {
    const prevAt = Date.parse(prevMeter.livePriceAt || '') || 0
    const nextAt = Date.parse(nextMeter.meter.livePriceAt || '') || 0
    if (prevAt >= nextAt) {
      nextMeter.livePrice = prevMeter.livePrice
      nextMeter.livePriceAt = prevMeter.livePriceAt
    }
  }
}

function isEmptyLiveValue(field) {
  const value = field?.displayedAs ?? field?.value

  return value == null || value === '' || value === '—'
}

function statusOf(resource) {
  return fieldByAttribute(resource, 'status')?.value
}

function closeness(resource) {
  const pct = parseDistance(fieldByAttribute(resource, 'distance_to_entry')?.value)

  return pct === null ? Number.POSITIVE_INFINITY : Math.abs(pct)
}

/**
 * Keep the index on a fixed page window (e.g. 25 rows).
 *
 * A Pusher-driven refresh already returns the current page from the API;
 * this trims any overflow and reports which ids entered / left so the UI
 * can animate only those rows — new row in, last row of the page out;
 * row gone, first of the next page lands at the bottom when the API
 * included it.
 */
export function slidePageWindow(previous, next, limit) {
  const cap = Math.max(1, Number(limit) || 25)
  const incoming = Array.isArray(next) ? next : []
  const prev = Array.isArray(previous) ? previous : []
  const windowed = incoming.slice(0, cap)

  const prevIds = new Set(prev.map(row => String(row?.id?.value)))
  const nextIds = new Set(windowed.map(row => String(row?.id?.value)))

  const entered = []
  const exited = []

  for (const row of windowed) {
    const id = String(row?.id?.value)
    if (!prevIds.has(id)) entered.push(id)
  }

  for (const row of prev) {
    const id = String(row?.id?.value)
    if (!nextIds.has(id)) exited.push(id)
  }

  return { resources: windowed, entered, exited }
}

/**
 * Same ranking as MainPoint::defaultOrderings — opened, then watching by
 * how close live price is to entry, then everything already settled.
 */
export function sortMainPoints(resources) {
  const rank = { opened: 0, watching: 1 }

  return [...resources].sort((a, b) => {
    const ra = rank[statusOf(a)] ?? 2
    const rb = rank[statusOf(b)] ?? 2
    if (ra !== rb) return ra - rb

    if (ra === 1) {
      const da = closeness(a)
      const db = closeness(b)
      if (da !== db) return da - db
    }

    return Number(b.id.value) - Number(a.id.value)
  })
}

export function walkPanels(panels, fn) {
  for (const panel of panels || []) {
    for (const field of panel.fields || []) {
      fn(field, panel)
    }
  }
}

export function applyPricesToDetail(resource, panels, resourceName, prices) {
  const bundle = { fields: resource?.fields || [], panels: panels || [] }
  const tick = tickFor(
    {
      ...resource,
      fields: [...(resource?.fields || []), ...(panels || []).flatMap(panel => panel.fields || [])],
    },
    resourceName,
    prices,
  )
  if (!tick) return false

  const live = Number(tick.last_price)
  if (!(live > 0)) return false

  let changed = false
  const apply = field => {
    if (field.attribute === 'last_price') {
      if (field.value !== live) {
        field.value = live
        field.displayedAs = live
        changed = true
      }
    }

    if (field.attribute === 'live_price') {
      const label = String(live)
      if (field.value !== label) {
        field.value = label
        field.displayedAs = label
        changed = true
      }
    }

    if (field.attribute === 'distance_to_entry' && resourceName === 'main-points') {
      const entryField = [...(resource?.fields || []), ...(panels || []).flatMap(p => p.fields || [])].find(
        candidate => candidate.attribute === 'price',
      )
      const entry = Number(entryField?.value)
      if (entry > 0) {
        const next = formatDistance(((live - entry) / entry) * 100)
        if (field.value !== next) {
          field.value = next
          field.displayedAs = next
          changed = true
        }
      }
    }
  }

  for (const field of resource?.fields || []) apply(field)
  walkPanels(panels, apply)

  return changed
}
